'use strict';

(function initializeNpcWorkshop(root) {
    const app = root.dndNextLevel;
    const state = app.npcWorkshopState;
    const availableStorage = name => {
        try { return root[name]; } catch (_error) { return null; }
    };
    const storage = availableStorage('localStorage');
    const sessionStorage = availableStorage('sessionStorage');
    const workshop = {activeSection: null, currentDocument: null};
    let statblockSwapVersion = 0;
    let portraitPreloadPending = false;

    const isStatblockTarget = event => event.detail?.target?.id === 'npc-workshop-statblock';
    const portraitReady = portrait => new Promise(resolve => {
        const image = new Image();
        let done = false;
        const finish = () => {
            if (done) return;
            done = true;
            root.clearTimeout(timeout);
            resolve();
        };
        const source = portrait.closest('picture')?.querySelector('source[data-srcset]');
        if (source) {
            image.sizes = source.sizes;
            image.srcset = source.dataset.srcset;
        }
        image.addEventListener('load', finish, {once: true});
        image.addEventListener('error', finish, {once: true});
        const timeout = root.setTimeout(finish, 10000);
        image.src = portrait.dataset.src;
        if (image.decode) image.decode().then(finish, finish);
    });

    const panels = {
        update() {
            const specie = document.getElementById('npc-workshop-specie');
            const profession = document.getElementById('npc-workshop-profession');
            const villain = document.getElementById('npc-workshop-villain');
            document.querySelector('[data-homebrew-panel="specie"]')?.classList.toggle('hidden', specie?.value !== 'HOMEBREW');
            document.querySelector('[data-homebrew-panel="profession"]')?.classList.toggle('hidden', profession?.value !== 'HOMEBREW');
            document.querySelector('[data-villain-panel]')?.classList.toggle('hidden', !villain?.checked);
            panels.updateVillainDependencies();
        },
        updateVillainDependencies() {
            const filter = (childId, parentId, noneValue = null) => {
                const child = document.getElementById(childId);
                const parent = document.getElementById(parentId);
                if (!child || !parent) return;
                const hasDetails = Array.from(child.options).some(option => option.dataset.parent === parent.value);
                Array.from(child.options).forEach(option => {
                    const neutral = !option.dataset.parent;
                    const visible = parent.value === 'RANDOM' || option.dataset.parent === parent.value
                        || (neutral && option.value !== noneValue) || (option.value === noneValue && !hasDetails);
                    option.hidden = !visible;
                    option.disabled = !visible;
                });
                if (child.selectedOptions[0]?.disabled) child.value = 'RANDOM';
            };
            filter('npc-workshop-villain-scheme', 'npc-workshop-villain-objective');
            filter('npc-workshop-villain-method-detail', 'npc-workshop-villain-method', 'NONE');
        },
    };

    const navigation = {
        behavior: () => root.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        select(section) {
            if (!section) return;
            workshop.activeSection = section;
            const button = document.getElementById('npc-workshop-edit-options');
            if (button) {
                button.textContent = `Edit ${section.dataset.npcWorkshopSectionLabel} options`;
                button.setAttribute('aria-controls', section.id);
            }
        },
        start() {
            const sections = Array.from(document.querySelectorAll('[data-npc-workshop-section]'));
            if (!sections.length) return;
            navigation.select(sections.find(section => section.open) || sections[0]);
            sections.forEach(section => {
                if (section.dataset.npcNavigationBound) return;
                section.dataset.npcNavigationBound = 'true';
                section.querySelector('summary')?.addEventListener('click', event => {
                    event.preventDefault();
                    sections.forEach(candidate => candidate.open = candidate === section);
                    navigation.select(section);
                });
            });
            const edit = document.getElementById('npc-workshop-edit-options');
            const view = document.getElementById('npc-workshop-view-result');
            if (edit && !edit.dataset.bound) {
                edit.dataset.bound = 'true';
                edit.addEventListener('click', () => workshop.activeSection?.querySelector('summary')?.scrollIntoView({behavior: navigation.behavior(), block: 'start'}));
            }
            if (view && !view.dataset.bound) {
                view.dataset.bound = 'true';
                view.addEventListener('click', () => document.getElementById('npc-workshop-statblock')?.scrollIntoView({behavior: navigation.behavior(), block: 'center'}));
            }
        },
    };

    const history = {
        read: () => state.readHistory(storage),
        write(items) {
            state.writeHistory(storage, items);
            history.render(items);
            const field = document.querySelector('[data-npc-workshop-history-field]');
            if (field) field.value = JSON.stringify(items);
        },
        render(items = history.read()) {
            const list = document.querySelector('[data-npc-workshop-history-list]');
            if (!list) return;
            list.replaceChildren();
            items.forEach((item, index) => {
                const button = document.createElement('button');
                button.type = 'button';
                button.dataset.restoreNpcIndex = String(index);
                button.className = 'min-h-11 w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 text-left hover:border-rose-700 dark:border-zinc-700 dark:bg-zinc-950';
                const name = document.createElement('strong');
                name.className = 'block text-rose-900 dark:text-rose-300';
                name.textContent = state.displayName(item.npc) || 'Unnamed NPC';
                const detail = document.createElement('span');
                detail.className = 'block text-xs text-slate-600 dark:text-slate-300';
                detail.textContent = [item.npc?.specieLabel || item.npc?.specie, item.npc?.occupation || item.npc?.characterClass].filter(Boolean).join(' · ');
                button.append(name, detail);
                const row = document.createElement('li');
                row.append(button);
                list.append(row);
            });
            if (!items.length) {
                const empty = document.createElement('li');
                empty.className = 'text-sm text-slate-600 dark:text-slate-300';
                empty.textContent = 'Generated characters appear here.';
                list.append(empty);
            }
        },
        accept(documentValue) {
            workshop.currentDocument = documentValue;
            const workspace = document.getElementById('npc-workshop-workspace');
            let items;
            if (workspace?.dataset.npcWorkshopHistoryDocument) {
                try { items = JSON.parse(workspace.dataset.npcWorkshopHistoryDocument); }
                catch (_error) { items = []; }
            } else {
                items = workspace?.dataset.npcWorkshopClearHistory === 'true' ? [] : history.read();
                items = state.remember(items, documentValue.workshop);
            }
            history.write(Array.isArray(items) ? items : []);
            if (storage) app.npcWorkshopRecentAdapter?.saveCurrent(documentValue, storage);
            const status = document.querySelector('[data-npc-workshop-save-status]');
            if (status) status.textContent = 'Current workshop saved in this browser.';
        },
        restore(index) {
            const item = history.read()[index];
            if (!item || !workshop.currentDocument) return;
            const documentValue = {...workshop.currentDocument, workshop: item};
            const form = document.querySelector('[data-persisted-fragment-restore]');
            const input = form?.querySelector('[name="document"]');
            if (!form || !input) return;
            input.value = JSON.stringify(documentValue);
            form.requestSubmit();
        },
    };

    const initializeViews = () => {
        const layout = document.querySelector('[data-component="controls-result-layout"]');
        layout?.querySelectorAll('[data-workbench-view-target]').forEach(button => {
            if (button.dataset.bound) return;
            button.dataset.bound = 'true';
            button.addEventListener('click', () => {
                layout.querySelectorAll('[data-workbench-view]').forEach(panel => panel.classList.toggle('hidden', panel.dataset.workbenchView !== button.dataset.workbenchViewTarget));
                layout.querySelectorAll('[data-workbench-view-target]').forEach(candidate => candidate.setAttribute('aria-pressed', String(candidate === button)));
            });
        });
    };
    const initializeDialogs = () => {
        const dialog = document.getElementById('npc-workshop-reset-dialog');
        const open = document.getElementById('npc-workshop-reset');
        const cancel = document.getElementById('npc-workshop-reset-cancel');
        if (open && !open.dataset.bound) {
            open.dataset.bound = 'true';
            open.addEventListener('click', () => dialog?.showModal());
        }
        if (cancel && !cancel.dataset.bound) {
            cancel.dataset.bound = 'true';
            cancel.addEventListener('click', () => dialog?.close());
        }
    };
    const initializeUi = () => {
        const active = workshop.activeSection?.id ? document.getElementById(workshop.activeSection.id) : null;
        if (active) {
            document.querySelectorAll('[data-npc-workshop-section]').forEach(section => section.open = section === active);
        }
        panels.update();
        navigation.start();
        initializeViews();
        initializeDialogs();
        history.render();
    };
    const setLoading = loading => {
        const button = document.getElementById('npc-workshop-reroll');
        if (!button) return;
        button.disabled = loading;
        const label = button.querySelector('[data-reroll-label]');
        if (label) label.textContent = loading ? 'Rerolling unlocked…' : 'Reroll unlocked';
    };
    const copyBiography = async button => {
        const publicSummary = document.querySelector('[data-biography-public]')?.textContent?.trim();
        const dmNotes = document.querySelector('[data-biography-dm]')?.textContent?.trim();
        if (!publicSummary || !navigator.clipboard) return;
        await navigator.clipboard.writeText(dmNotes ? `${publicSummary}\n\nDM Notes: ${dmNotes}` : publicSummary);
        const label = button.textContent;
        button.textContent = 'Copied';
        root.setTimeout(() => button.textContent = label, 1500);
    };
    const clearPrintedStatblock = () => {
        const printRoot = document.querySelector('[data-npc-statblock-print-root]');
        if (printRoot) printRoot.remove();
        document.body.classList.remove('npc-workshop-printing');
    };
    const printStatblock = () => {
        const statblock = document.querySelector('[data-statblock-summary-card]');
        if (!statblock) return;
        clearPrintedStatblock();
        const printRoot = document.createElement('div');
        printRoot.dataset.npcStatblockPrintRoot = '';
        printRoot.append(statblock.cloneNode(true));
        document.body.append(printRoot);
        document.body.classList.add('npc-workshop-printing');
        const printablePageHeight = 270 * 96 / 25.4;
        const scale = Math.min(1, printablePageHeight / printRoot.scrollHeight);
        printRoot.style.setProperty('--npc-statblock-print-scale', scale.toFixed(4));
        root.print();
    };

    document.addEventListener('DOMContentLoaded', () => {
        if (storage) {
            app.npcWorkshopRecentAdapter?.migrateLegacy(storage);
            if (sessionStorage) app.npcWorkshopRecentAdapter?.consumeRestore(storage, sessionStorage);
            state.migrateCurrent(storage);
        }
        app.persistedFragment?.start({
            storage,
            onStored: history.accept,
            restoreTimeoutMs: 10000,
            retainRenderedOnReject: true,
            onRestoreFailure: () => {
                const notice = document.getElementById('npc-workshop-restore-notice');
                if (notice) notice.hidden = false;
            },
        });
        initializeUi();
    });
    document.addEventListener('change', event => {
        if (event.target?.id === 'npc-workshop-villain-scheme') {
            const parent = event.target.selectedOptions[0]?.dataset.parent;
            if (parent) document.getElementById('npc-workshop-villain-objective').value = parent;
        }
        if (event.target?.id === 'npc-workshop-villain-method-detail') {
            const parent = event.target.selectedOptions[0]?.dataset.parent;
            if (parent) document.getElementById('npc-workshop-villain-method').value = parent;
        }
        if (['npc-workshop-specie', 'npc-workshop-profession', 'npc-workshop-villain'].includes(event.target?.id)) panels.update();
        if (event.target?.closest('[data-npc-workshop-history-field]')) event.target.value = JSON.stringify(history.read());
    }, true);
    document.addEventListener('htmx:beforeRequest', event => {
        if (isStatblockTarget(event)) statblockSwapVersion += 1;
        setLoading(true);
    });
    document.addEventListener('htmx:beforeSwap', event => {
        if (['npc-workshop-workspace', 'npc-workshop-statblock'].includes(event.detail?.target?.id)
                && event.detail.serverResponse) {
            event.detail.serverResponse = event.detail.serverResponse.replace(/<noscript>[\s\S]*?<\/noscript>/gi, '');
        }
        if (!isStatblockTarget(event)) return;
        const response = event.detail.serverResponse;
        const fragment = document.createElement('template');
        fragment.innerHTML = response;
        const portrait = fragment.content.querySelector('#npc-workshop-portrait img[data-src]');
        if (!portrait?.dataset.src || !root.htmx) return;

        event.detail.shouldSwap = false;
        portraitPreloadPending = true;
        const version = statblockSwapVersion;
        portraitReady(portrait).then(() => {
            if (version !== statblockSwapVersion) return;
            portraitPreloadPending = false;
            root.htmx.swap(event.detail.target, response, {swapStyle: 'outerHTML'});
            setLoading(false);
        });
    });
    document.addEventListener('htmx:afterRequest', event => {
        if (!isStatblockTarget(event) || !portraitPreloadPending) setLoading(false);
    });
    document.addEventListener('htmx:afterSwap', event => {
        if (event.target?.id === 'npc-workshop-workspace') initializeUi();
        if (['npc-workshop-workspace', 'npc-workshop-statblock'].includes(event.target?.id)) {
            document.getElementById('npc-workshop-statblock')?.focus({preventScroll: true});
            const status = document.getElementById('npc-workshop-update-status');
            if (status) root.requestAnimationFrame(() => status.textContent = `${workshop.activeSection?.dataset.npcWorkshopSectionLabel || 'NPC'} result updated.`);
        }
    });
    document.addEventListener('click', event => {
        if (event.target.closest('[data-retry-npc-restore]')) {
            const notice = document.getElementById('npc-workshop-restore-notice');
            if (app.persistedFragment?.start()?.retryRestore()) notice.hidden = true;
        }
        const restore = event.target.closest('[data-restore-npc-index]');
        if (restore) history.restore(Number(restore.dataset.restoreNpcIndex));
        const copy = event.target.closest('[data-copy-biography]');
        if (copy) copyBiography(copy);
        if (event.target.closest('[data-print-statblock]')) printStatblock();
        if (event.target.closest('[data-save-npc]') && workshop.currentDocument) {
            if (storage) app.npcWorkshopRecentAdapter?.saveCurrent(workshop.currentDocument, storage, true);
        }
    });
    root.addEventListener('afterprint', clearPrintedStatblock);
    app.npcWorkshop = workshop;
})(window);
